// src/pages/LorePage.js — v3 avec profils détaillés + glossaire
import React, { useState } from "react";
import { ONI_FORMS, STAT_META, ONI_IMG_VERSION} from "../data/demons";
import "./SimplePage.css";
import "./LorePage.css";

const GLOSSARY = [
  { term:"Takkyu", kanji:"卓球", def:"Tennis de table en japonais. Littéralement : sport de balle de table." },
  { term:"Oni", kanji:"鬼", def:"Démon ou ogre dans le folklore japonais. Créature surnaturelle incarnant la colère, la puissance brute, et le chaos." },
  { term:"Takkyu Oni", kanji:"卓球鬼", def:"Le démon du ping. L'archétype démoniaque qui sommeille en chaque joueur de tennis de table." },
  { term:"Aggression", kanji:"攻撃性", def:"La pulsion d'attaque. L'incapacité à laisser une balle sans l'écraser." },
  { term:"Chaos", kanji:"混沌", def:"La capacité à créer l'imprévisible. L'ennemi des joueurs méthodiques." },
  { term:"Tcho", kanji:"策略", def:"Terme argotique : la tchatche, la manipulation, l'excuse permanente. Arme des Seal Oni et Tanuki Oni." },
  { term:"Discipline", kanji:"規律", def:"Suivre son plan quoi qu'il arrive. La vertu des Turtle Oni et Spider Oni." },
  { term:"Perk", kanji:"特性", def:"Trait démoniaque spécifique qui modifie le profil du joueur. Chaque perk amplifie certaines stats." },
  { term:"Awakening", kanji:"覚醒", def:"Le moment où le joueur identifie son démon intérieur et l'accepte pleinement." },
  { term:"Horoscope Pongistique", kanji:"卓球占い", def:"Prédiction hebdomadaire basée sur l'archétype Oni du joueur. Chaque semaine, les astres du ping parlent." },
  { term:"Order/Chaos Axis", kanji:"秩序混沌軸", def:"L'axe fondamental. Lawful = discipline et contrôle. Chaos = créativité et imprévisibilité. Ni l'un ni l'autre n'est supérieur." },
  { term:"Picot", kanji:"粒高", def:"Revêtement à picots longs. Arme démoniaque du joueur chaotique. Légal. Douteux. Efficace." },
];

export default function LorePage() {
  const [activeOni, setActiveOni] = useState(null);
  const [lightbox, setLightbox]   = useState(null); // Oni en plein écran

  return (
    <div className="simple-page lore-page">
      <div className="section" style={{paddingTop:"8rem"}}>

        <div className="simple-header">
          <span className="section-eyebrow">伝説 · La Légende</span>
          <h1 className="section-title">Le Lore<br/>Takkyu Oni</h1>
          <div className="divider"/>
        </div>

        {/* Origin */}
        <div className="lore-origin">
          <div className="lore-block">
            <h2>L'Origine</h2>
            <p>Il est dit que le premier coup de raquette frappé dans la <span>rage pure</span> ouvrit une porte. Pas une porte vers la victoire — une porte vers l'autre côté. L'autre côté de la table. L'autre côté de soi.</p>
            <p>De l'autre côté vivent les <span>Takkyu Oni</span> : démons nés de chaque mauvaise excuse, de chaque service illégal assumé, de chaque rouleau qui passe sur le filet en finale.</p>
            <p>Ils ne sont pas tes ennemis. Ils <span>sont toi</span>. Le côté que tu n'avoues jamais au vestiaire. Le petit cri que tu retiens quand tu rates le coup droit facile à 10-9.</p>
          </div>

          <div className="lore-quote-block">
            <p className="lore-jp">「卓球の鬼は怒りから生まれる。<br/>しかし、その怒りこそが力だ。<br/>自分の鬼を知る者だけが、<br/>真の卓球師になれる。」</p>
            <p className="lore-fr">"Le démon du ping naît de la colère. Mais cette colère est sa force. Seul celui qui connaît son démon peut devenir un vrai maître de la balle."</p>
            <cite>— AINO, Démon en Chef · 悪魔長</cite>
          </div>
        </div>

        {/* Oni profiles */}
        <div style={{marginTop:"5rem"}}>
          <span className="section-eyebrow">原型 · Les 12 Archétypes</span>
          <h2 className="section-title" style={{marginBottom:"0.5rem"}}>Les Formes Oni</h2>
          <p className="section-sub" style={{marginBottom:"2rem"}}>
            Clique sur l'illustration pour l'agrandir. Clique sur la carte pour voir le profil complet.
          </p>
          <div className="divider"/>

          <div className="oni-profiles-grid">
            {ONI_FORMS.map(oni => (
              <div
                key={oni.id}
                className={`oni-profile-card${activeOni?.id === oni.id ? " active" : ""}`}
              >
                <div className="oni-profile-header">
                  {oni.image ? (
                    <div
                      className="oni-profile-img-wrap"
                      onClick={(e) => { e.stopPropagation(); setLightbox(oni); }}
                      title="Agrandir"
                    >
                      <img src={`${oni.image}?v=${ONI_IMG_VERSION}`} alt={oni.name} className="oni-profile-img"/>
                      <span className="oni-profile-zoom">⛶</span>
                    </div>
                  ) : (
                    <span className="oni-profile-emoji" onClick={(e) => { e.stopPropagation(); setLightbox(oni); }}>{oni.emoji}</span>
                  )}
                  <div onClick={() => setActiveOni(activeOni?.id === oni.id ? null : oni)} style={{cursor:"pointer", flex:1}}>
                    <span className="oni-profile-jp">{oni.jp}</span>
                    <span className="oni-profile-name">{oni.name}</span>
                    <span className="oni-profile-sign">{oni.sign} {oni.signName}</span>
                  </div>
                  <span className="oni-expand-icon" onClick={() => setActiveOni(activeOni?.id === oni.id ? null : oni)}>
                    {activeOni?.id === oni.id ? "−" : "+"}
                  </span>
                </div>
                <p className="oni-profile-tagline" onClick={() => setActiveOni(activeOni?.id === oni.id ? null : oni)} style={{cursor:"pointer"}}>
                  {oni.tagline}
                </p>

                {activeOni?.id === oni.id && (
                  <div className="oni-profile-detail">
                    <p className="oni-profile-desc">{oni.desc}</p>
                    <div className="oni-profile-stats">
                      {[oni.axisPrimary, oni.axisSecondary].map(stat => (
                        <div key={stat} className="oni-stat-highlight">
                          <span style={{color: STAT_META[stat]?.color}}>
                            {STAT_META[stat]?.label}
                          </span>
                          <div className="oni-stat-bar">
                            <div style={{width: oni.stats[stat] + "%", background: STAT_META[stat]?.color}} />
                          </div>
                          <span style={{color: STAT_META[stat]?.color, fontSize:"0.7rem"}}>{oni.stats[stat]}</span>
                        </div>
                      ))}
                    </div>
                    <p className="oni-stat-desc">
                      {STAT_META[oni.axisPrimary]?.desc} · {STAT_META[oni.axisSecondary]?.desc}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stats explained */}
        <div style={{marginTop:"5rem"}}>
          <span className="section-eyebrow">統計 · Les Stats</span>
          <h2 className="section-title">Système de Stats</h2>
          <div className="divider"/>
          <div className="stats-grid">
            {Object.entries(STAT_META).map(([key, meta]) => (
              <div key={key} className="stat-explainer">
                <div className="stat-explainer-header">
                  <span className="stat-explainer-dot" style={{background: meta.color}}/>
                  <span style={{color: meta.color, fontFamily:"var(--font-disp)", letterSpacing:"2px", fontSize:"0.85rem"}}>{meta.label}</span>
                </div>
                <p>{meta.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Glossary */}
        <div style={{marginTop:"5rem"}}>
          <span className="section-eyebrow">辞典 · Le Glossaire</span>
          <h2 className="section-title">Dictionnaire Démoniaque</h2>
          <div className="divider"/>
          <div className="glossary-list">
            {GLOSSARY.map(g => (
              <div key={g.term} className="glossary-entry">
                <div className="glossary-term">
                  <span className="glossary-word">{g.term}</span>
                  <span className="glossary-kanji">{g.kanji}</span>
                </div>
                <p className="glossary-def">{g.def}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Lightbox plein écran */}
      {lightbox && (
        <div className="oni-lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="oni-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <div className="oni-lightbox-content" onClick={e => e.stopPropagation()}>
            {lightbox.image ? (
              <img src={`${lightbox.image}?v=${ONI_IMG_VERSION}`} alt={lightbox.name} className="oni-lightbox-img"/>
            ) : (
              <span className="oni-lightbox-emoji">{lightbox.emoji}</span>
            )}
            <div className="oni-lightbox-info">
              <p className="oni-lightbox-jp">{lightbox.jp}</p>
              <p className="oni-lightbox-name">{lightbox.name}</p>
              <p className="oni-lightbox-tagline">"{lightbox.tagline}"</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
